import React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faMemoPad } from "@fortawesome/pro-regular-svg-icons";
import { faCheck, faRotate, faUsers } from "@fortawesome/pro-solid-svg-icons";

import { Icon, Text } from "@performyard/py-components";

import type { CycleType, CycleTypeOption } from "../Config";
import { CYCLE_TYPE_ICON_COLORS } from "../Config";

type CycleTypeCardProps = {
	option: CycleTypeOption;
	selected: boolean;
	disabled?: boolean;
};

const CYCLE_TYPE_ICONS: Record<CycleType, IconDefinition> = {
	manual: faMemoPad,
	automated: faRotate,
	group: faUsers,
};

export const CycleTypeCard = ({ option, selected, disabled = false }: CycleTypeCardProps) => {
	const icon = CYCLE_TYPE_ICONS[option.type];
	const iconBg = CYCLE_TYPE_ICON_COLORS[option.type];

	return (
		<div
			className={`box-border flex w-[357px] shrink-0 flex-col gap-8 rounded bg-white p-6 text-left ${
				selected
					? "border border-solid border-primary-5 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
					: "border border-solid border-transparent"
			} ${disabled ? "cursor-not-allowed opacity-disabled" : "cursor-pointer"}`}
		>
			{/* Header: icon badge + title + radio indicator */}
			<div className="flex w-full items-center gap-3">
				<div className={`flex h-8 w-[30px] shrink-0 items-center justify-center rounded ${iconBg}`}>
					<Icon icon={icon} color="neutral-0" size={16} />
				</div>
				<Text size="sub-heading" weight="semi-bold" color="neutral-9" dangerouslySetClassName="flex-1">
					{option.title}
				</Text>
				<div
					className={`flex size-6 shrink-0 items-center justify-center rounded-full border border-solid ${
						selected ? "border-primary-5 bg-primary-5" : "border-neutral-3 bg-transparent"
					}`}
				>
					{selected && <div className="block size-2 rounded-full bg-neutral-0" />}
				</div>
			</div>

			{/* Description */}
			<Text size="paragraph-3" color="neutral-9" dangerouslySetClassName="h-12">
				{option.description}
			</Text>

			{/* Feature list */}
			<div className="flex flex-col gap-1 pb-8">
				{option.features.map((feature) => (
					<div key={feature} className="flex items-center gap-2">
						<Icon icon={faCheck} color="green-5" size={20} />
						<Text size="paragraph-3" color="neutral-7" dangerouslySetClassName="truncate">
							{feature}
						</Text>
					</div>
				))}
			</div>
		</div>
	);
};
